/**
 * Watrly Dropdown Component
 * =========================
 * مكون القائمة المنسدلة مع البحث
 */

import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useTheme } from '../../context/ThemeContext';
import './Dropdown.css';

const Dropdown = ({
  options = [],
  value,
  onChange,
  placeholder,
  label,
  searchable = false,
  disabled = false,
  error,
  hint,
  size = 'md',
  fullWidth = true,
  className = '',
  id,
  name,
  renderOption,
  emptyMessage,
  ...props
}) => {
  const { isRTL, t } = useTheme();
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [dropdownPosition, setDropdownPosition] = useState('bottom');

  const containerRef = useRef(null);
  const inputRef = useRef(null);
  const listRef = useRef(null);

  const inputId = id || name || `dropdown-${Math.random().toString(36).substr(2, 9)}`;

  // البحث في الخيارات
  const filteredOptions = searchable && searchTerm
    ? options.filter(opt => 
        opt.label.toLowerCase().includes(searchTerm.toLowerCase())
      )
    : options;

  // الحصول على الخيار المحدد
  const selectedOption = options.find(opt => opt.value === value);

  // حساب موضع القائمة ديناميكياً
  const calculatePosition = useCallback(() => {
    if (!containerRef.current) return;

    const rect = containerRef.current.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom;
    const spaceAbove = rect.top;
    const dropdownHeight = Math.min(filteredOptions.length * 44 + 16, 300);

    if (spaceBelow < dropdownHeight && spaceAbove > spaceBelow) {
      setDropdownPosition('top');
    } else {
      setDropdownPosition('bottom');
    }
  }, [filteredOptions.length]);

  // فتح/إغلاق القائمة
  const toggleDropdown = () => {
    if (disabled) return;
    setIsOpen(prev => {
      if (!prev) {
        calculatePosition();
        setHighlightedIndex(-1);
        setSearchTerm('');
      }
      return !prev;
    });
  };

  // اختيار خيار
  const selectOption = (option) => {
    onChange?.(option.value, option);
    setIsOpen(false);
    setSearchTerm('');
  };

  // التنقل بالكيبورد
  const handleKeyDown = (e) => {
    if (disabled) return;

    switch (e.key) {
      case 'Enter':
        e.preventDefault();
        if (isOpen && highlightedIndex >= 0) {
          selectOption(filteredOptions[highlightedIndex]);
        } else {
          toggleDropdown();
        }
        break;
      case 'Escape':
        setIsOpen(false);
        break;
      case 'ArrowDown':
        e.preventDefault();
        if (!isOpen) {
          setIsOpen(true);
          calculatePosition();
        } else {
          setHighlightedIndex(prev => 
            prev < filteredOptions.length - 1 ? prev + 1 : 0
          );
        }
        break;
      case 'ArrowUp':
        e.preventDefault();
        if (isOpen) {
          setHighlightedIndex(prev => 
            prev > 0 ? prev - 1 : filteredOptions.length - 1
          );
        }
        break;
      default:
        break;
    }
  };

  // إغلاق عند النقر خارج المكون
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // التمرير للخيار المحدد
  useEffect(() => {
    if (isOpen && highlightedIndex >= 0 && listRef.current) {
      const item = listRef.current.children[highlightedIndex];
      if (item) {
        item.scrollIntoView({ block: 'nearest' });
      }
    }
  }, [highlightedIndex, isOpen]);

  const containerClasses = [
    'watrly-dropdown',
    `watrly-dropdown--${size}`,
    fullWidth && 'watrly-dropdown--full',
    disabled && 'watrly-dropdown--disabled',
    error && 'watrly-dropdown--error',
    isOpen && 'watrly-dropdown--open',
    className,
  ].filter(Boolean).join(' ');

  return (
    <div 
      ref={containerRef}
      className={containerClasses} 
      dir={isRTL ? 'rtl' : 'ltr'}
      {...props}
    >
      {label && (
        <label htmlFor={inputId} className="watrly-dropdown__label">
          {label}
        </label>
      )}

      <div 
        className="watrly-dropdown__control"
        onClick={toggleDropdown}
        onKeyDown={handleKeyDown}
        tabIndex={disabled ? -1 : 0}
        role="combobox"
        aria-expanded={isOpen}
        aria-haspopup="listbox"
        aria-controls={`${inputId}-list`}
      >
        <span className={`watrly-dropdown__value ${!selectedOption ? 'watrly-dropdown__placeholder' : ''}`}>
          {selectedOption ? selectedOption.label : placeholder || t?.('general.select') || 'اختر'}
        </span>

        <span className="watrly-dropdown__arrow">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <polyline points="6 9 12 15 18 9" />
          </svg>
        </span>
      </div>

      {isOpen && (
        <div 
          className={`watrly-dropdown__menu watrly-dropdown__menu--${dropdownPosition}`}
        >
          {searchable && (
            <div className="watrly-dropdown__search">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="11" cy="11" r="8" />
                <line x1="21" y1="21" x2="16.65" y2="16.65" />
              </svg>
              <input
                ref={inputRef}
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder={t?.('general.search') || 'بحث...'}
                className="watrly-dropdown__search-input"
                autoFocus
              />
            </div>
          )}

          <ul
            ref={listRef}
            id={`${inputId}-list`}
            className="watrly-dropdown__list"
            role="listbox"
          >
            {filteredOptions.length > 0 ? (
              filteredOptions.map((option, index) => (
                <li
                  key={option.value}
                  className={[
                    'watrly-dropdown__option',
                    option.value === value && 'watrly-dropdown__option--selected',
                    index === highlightedIndex && 'watrly-dropdown__option--highlighted',
                    option.disabled && 'watrly-dropdown__option--disabled',
                  ].filter(Boolean).join(' ')}
                  onClick={() => !option.disabled && selectOption(option)}
                  onMouseEnter={() => setHighlightedIndex(index)}
                  role="option"
                  aria-selected={option.value === value}
                >
                  {renderOption ? renderOption(option) : (
                    <>
                      {option.icon && <span className="watrly-dropdown__option-icon">{option.icon}</span>}
                      <span className="watrly-dropdown__option-label">{option.label}</span>
                      {option.value === value && (
                        <svg className="watrly-dropdown__check" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                          <polyline points="20 6 9 17 4 12" />
                        </svg>
                      )}
                    </>
                  )}
                </li>
              ))
            ) : (
              <li className="watrly-dropdown__empty">
                {emptyMessage || t?.('general.noResults') || 'لا توجد نتائج'}
              </li>
            )}
          </ul>
        </div>
      )}

      {error && (
        <p className="watrly-dropdown__error" role="alert">
          {error}
        </p>
      )}

      {hint && !error && (
        <p className="watrly-dropdown__hint">
          {hint}
        </p>
      )}
    </div>
  );
};

export default Dropdown;
